import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { searchCatalogModels, clearSearchResults } from "../machineCatalogSlice";
import { CatalogModelListItem, MachineCatalogState } from "../models/models";
import { dt } from "../../../shared/themes/redesignTokens";
import { formatManufacturerWithHierarchy } from "../../../shared/utils/machineUtils";
import useTranslation from "../../../shared/hooks/useTranslation";

interface CatalogModelSearchProps {
  onSelect: (model: CatalogModelListItem) => void;
  selectedModel?: CatalogModelListItem | null;
  onClear?: () => void;
}

const CatalogModelSearch: React.FC<CatalogModelSearchProps> = ({
  onSelect,
  selectedModel,
  onClear,
}) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");
  const isPhone = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:1200px)");

  const { searchResults, searchTotalCount, searchLoading } =
    useAppSelector<MachineCatalogState>((state: any) => state.machineCatalog);

  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleSearch = useCallback(
    (text: string) => {
      if (text.trim().length < 2) {
        dispatch(clearSearchResults());
        setShowDropdown(false);
        setHighlightedIndex(-1);
        return;
      }
      dispatch(searchCatalogModels({ search: text.trim(), pageSize: 20 }));
      setShowDropdown(true);
    },
    [dispatch]
  );

  const handleInputChange = (value: string) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) {
      dispatch(clearSearchResults());
      setShowDropdown(false);
      setHighlightedIndex(-1);
      return;
    }
    debounceRef.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  const handleSelectModel = (model: CatalogModelListItem) => {
    setSearchText("");
    setShowDropdown(false);
    dispatch(clearSearchResults());
    setHighlightedIndex(-1);
    onSelect(model);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || searchResults.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case "Enter":
        if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
          e.preventDefault();
          handleSelectModel(searchResults[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedIndex]);

  // If a model is selected, show a compact indicator
  if (selectedModel) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: dt.blueSoft,
          border: `1.5px solid ${dt.blue}`,
          borderRadius: dt.radiusSm,
          padding: isPhone ? "10px 12px" : "12px 16px",
          transition: "all 0.2s ease",
        }}
      >
        <PrecisionManufacturingOutlinedIcon
          sx={{ color: dt.blue, fontSize: 20 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: dt.textPrimary,
              fontFamily: dt.font,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedModel.modelName}
          </Box>
          <Box
            sx={{
              fontSize: "11px",
              color: dt.textSecondary,
              fontFamily: dt.font,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedModel.manufacturer
              ? formatManufacturerWithHierarchy(
                  selectedModel.manufacturer.name,
                  selectedModel.manufacturer.parentManufacturerName
                )
              : ""}
            {selectedModel.categoryCode ? ` - ${selectedModel.categoryCode}` : ""}
            {selectedModel.numAxes ? ` - ${translate("axisCount", { count: selectedModel.numAxes })}` : ""}
          </Box>
        </Box>
        {onClear && (
          <Box
            onClick={onClear}
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: dt.blue,
              cursor: "pointer",
              fontFamily: dt.font,
              whiteSpace: "nowrap",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {translate("changeMachine")}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={{ position: "relative" }}>
      {/* Search input */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          background: dt.inputBg,
          borderRadius: dt.radiusSm,
          border: "1.5px solid transparent",
          padding: "0 12px",
          height: "44px",
          transition: "all 0.2s ease",
          "&:focus-within": {
            border: `1.5px solid ${dt.blue}`,
            boxShadow: `0 0 0 3px ${dt.blueGlow}`,
          },
        }}
      >
        <SearchOutlinedIcon
          sx={{ color: dt.textMuted, fontSize: 20, mr: 1, flexShrink: 0 }}
        />
        <input
          type="text"
          value={searchText}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchResults.length > 0) setShowDropdown(true);
          }}
          placeholder={translate("searchCatalogModels")}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            fontSize: "14px",
            fontFamily: 'var(--font-sans)',
            color: dt.textPrimary,
            background: "transparent",
          }}
        />
        {searchLoading && (
          <CircularProgress
            size={16}
            sx={{ color: dt.textMuted, flexShrink: 0 }}
          />
        )}
      </Box>

      {/* Dropdown results */}
      {showDropdown && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 10,
            backgroundColor: dt.cardBg,
            borderRadius: dt.radiusSm,
            border: `1px solid ${dt.cardBorder}`,
            boxShadow:
              "0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
            mt: 0.5,
            maxHeight: isPhone ? "260px" : "340px",
            overflowY: "auto",
          }}
        >
          {searchResults.length === 0 && !searchLoading && searchText.trim().length >= 2 && (
            <Box
              sx={{
                padding: "16px",
                textAlign: "center",
                fontSize: "13px",
                color: dt.textMuted,
                fontFamily: dt.font,
              }}
            >
              {translate("noModelsFound")}
            </Box>
          )}

          {searchResults.length > 0 && (
            <>
              {/* Results count */}
              <Box
                sx={{
                  padding: "8px 14px",
                  fontSize: "11px",
                  color: dt.textMuted,
                  fontFamily: dt.fontMono,
                  borderBottom: `1px solid ${dt.rowBorder}`,
                }}
              >
                {translate("modelsFoundCount", { count: searchTotalCount })}
              </Box>

              {searchResults.map((model, index) => (
                <Box
                  key={model.id}
                  ref={(el: HTMLDivElement | null) => {
                    itemRefs.current[index] = el;
                  }}
                  onClick={() => handleSelectModel(model)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    padding: isPhone ? "10px 12px" : "10px 14px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    backgroundColor:
                      index === highlightedIndex ? dt.blueGlow : "transparent",
                    "&:hover": {
                      backgroundColor: dt.blueGlow,
                    },
                    "&:not(:last-child)": {
                      borderBottom: `1px solid ${dt.rowBorder}`,
                    },
                  }}
                >
                  {/* Thumbnail image or placeholder icon */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: dt.radiusSm,
                      backgroundColor: dt.inputBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      overflow: "hidden",
                    }}
                  >
                    {(model.imageStandardUrl || model.imageUrl) ? (
                      <img
                        src={model.imageStandardUrl || model.imageUrl || ""}
                        alt={model.modelName}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <PrecisionManufacturingOutlinedIcon
                        sx={{ fontSize: 18, color: dt.textMuted }}
                      />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: dt.textPrimary,
                        fontFamily: dt.font,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {model.modelName}
                    </Box>
                    <Box
                      sx={{
                        fontSize: "11px",
                        color: dt.textSecondary,
                        fontFamily: dt.font,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 0.25,
                      }}
                    >
                      <span>
                        {model.manufacturer
                          ? formatManufacturerWithHierarchy(
                              model.manufacturer.name,
                              model.manufacturer.parentManufacturerName
                            )
                          : ""}
                      </span>
                      {model.categoryCode && (
                        <>
                          <span style={{ color: dt.textMuted }}>|</span>
                          <span>{model.categoryCode}</span>
                        </>
                      )}
                      {model.numAxes && (
                        <>
                          <span style={{ color: dt.textMuted }}>|</span>
                          <span>
                            {translate("axisCount", { count: model.numAxes })}
                          </span>
                        </>
                      )}
                    </Box>
                  </Box>
                  {/* Specs count badge */}
                  {model.specsCount > 0 && !isPhone && (
                    <Box
                      sx={{
                        fontSize: "10px",
                        fontFamily: dt.fontMono,
                        color: dt.textMuted,
                        backgroundColor: dt.inputBg,
                        padding: "2px 8px",
                        borderRadius: dt.radiusPill,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {model.specsCount} specs
                    </Box>
                  )}
                </Box>
              ))}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CatalogModelSearch;
